import { useCallback, useMemo, useState } from "react";
import { useSubmitHssResponses } from "@/common/hooks/use-submit-hss-responses";
import type { Asset, MyStatusAssetResponse, CampaignSummary } from "@amzn/hardware-self-service-api-model";
import type {
  MyStatus,
  PickupAddress,
  ResponseInput,
  SubmitResponsesRequest,
} from "@/services/hardware-self-service/hardware-self-service.types";
import { RETURN_PICKUP_RESPONSE, type CampaignResponseValue } from "../response-options";
import {
  buildInitialPickupAddress,
  buildServerSnapshotSignature,
  isPickupAddressValid,
} from "../campaigns.util";

/** Keyed per rendered row rather than by serial number, which can be missing or duplicated. */
export interface AssetRow {
  asset: Asset;
  key: string;
}

interface UseCampaignResponseFormParams {
  campaign?: CampaignSummary;
  assets: Asset[];
  existingResponses: Record<string, MyStatusAssetResponse>;
  existingPickupAddress?: MyStatus["pickupAddress"];
  login: string;
}

interface UseCampaignResponseFormResult {
  rows: AssetRow[];
  responses: Record<string, CampaignResponseValue>;
  remarks: Record<string, string>;
  pickupAddress: PickupAddress;
  respondedCount: number;
  isEveryRowAnswered: boolean;
  isReadyToSave: boolean;
  isSubmitting: boolean;
  submitError: Error | null;
  hasSubmitSucceeded: boolean;
  /** Increments once per completed save, so each save can replace the banner left by the previous one. */
  saveAttemptCount: number;
  onResponseChange: (rowKey: string, value: CampaignResponseValue) => void;
  onRemarksChange: (rowKey: string, value: string) => void;
  onPickupAddressChange: (address: PickupAddress) => void;
  saveResponses: () => Promise<void>;
  acknowledgeNoAssets: () => Promise<void>;
}

function buildRows(assets: Asset[]): AssetRow[] {
  return assets.map((asset, index) => ({
    asset,
    key: `${asset.serialNumber || "asset"}-${index}`,
  }));
}

function findExistingResponse(
  row: AssetRow,
  existingResponses: Record<string, MyStatusAssetResponse>
): MyStatusAssetResponse | undefined {
  return row.asset.serialNumber ? existingResponses[row.asset.serialNumber] : undefined;
}

function buildInitialResponses(
  rows: AssetRow[],
  existingResponses: Record<string, MyStatusAssetResponse>
): Record<string, CampaignResponseValue> {
  return Object.fromEntries(
    rows.map((row) => [
      row.key,
      (findExistingResponse(row, existingResponses)?.response ?? "") as CampaignResponseValue,
    ])
  );
}

function buildInitialRemarks(
  rows: AssetRow[],
  existingResponses: Record<string, MyStatusAssetResponse>
): Record<string, string> {
  return Object.fromEntries(
    rows
      .map((row) => [row.key, findExistingResponse(row, existingResponses)?.remarks] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[1]))
  );
}

/**
 * Owns the fields that mirror server state and re-seeds them whenever my-status returns a different
 * snapshot, so the form always reflects what HSS actually holds. Without this, an address that a
 * save deleted stays on screen until the form remounts. The comparison is on content because
 * my-status hands down a freshly allocated responses object on every render, and the re-seed happens
 * during render rather than in an effect so stale values are never painted.
 */
function useServerSeededFormState(
  rows: AssetRow[],
  existingResponses: Record<string, MyStatusAssetResponse>,
  existingPickupAddress?: MyStatus["pickupAddress"]
) {
  const [responses, setResponses] = useState(() => buildInitialResponses(rows, existingResponses));
  const [remarks, setRemarks] = useState(() => buildInitialRemarks(rows, existingResponses));
  const [pickupAddress, setPickupAddress] = useState<PickupAddress>(() =>
    buildInitialPickupAddress(existingPickupAddress)
  );
  const serverSnapshotSignature = buildServerSnapshotSignature(
    existingResponses,
    existingPickupAddress
  );
  const [seededSnapshotSignature, setSeededSnapshotSignature] = useState(serverSnapshotSignature);

  if (serverSnapshotSignature !== seededSnapshotSignature) {
    setSeededSnapshotSignature(serverSnapshotSignature);
    setResponses(buildInitialResponses(rows, existingResponses));
    setRemarks(buildInitialRemarks(rows, existingResponses));
    setPickupAddress(buildInitialPickupAddress(existingPickupAddress));
  }

  return { responses, setResponses, remarks, setRemarks, pickupAddress, setPickupAddress };
}

export function useCampaignResponseForm({
  campaign,
  assets,
  existingResponses,
  existingPickupAddress,
  login,
}: UseCampaignResponseFormParams): UseCampaignResponseFormResult {
  const { mutateAsync, isPending, error } = useSubmitHssResponses();
  const rows = useMemo(() => buildRows(assets), [assets]);
  const { responses, setResponses, remarks, setRemarks, pickupAddress, setPickupAddress } =
    useServerSeededFormState(rows, existingResponses, existingPickupAddress);
  const [hasSubmitSucceeded, setHasSubmitSucceeded] = useState(false);
  const [saveAttemptCount, setSaveAttemptCount] = useState(0);

  const onResponseChange = useCallback((rowKey: string, value: CampaignResponseValue) => {
    setResponses((previous) => ({ ...previous, [rowKey]: value }));
  }, []);

  const onRemarksChange = useCallback((rowKey: string, value: string) => {
    setRemarks((previous) => ({ ...previous, [rowKey]: value }));
  }, []);

  const onPickupAddressChange = useCallback((address: PickupAddress) => {
    setPickupAddress(address);
  }, []);

  const submit = useCallback(
    async (request: SubmitResponsesRequest) => {
      try {
        await mutateAsync(request);
        setHasSubmitSucceeded(true);
      } catch {
        setHasSubmitSucceeded(false);
      } finally {
        setSaveAttemptCount((previous) => previous + 1);
      }
    },
    [mutateAsync]
  );

  const saveResponses = useCallback(async () => {
    const campaignId = campaign?.id;
    if (!campaignId) {
      return;
    }
    const responseInputs = rows.reduce<ResponseInput[]>((inputs, row) => {
      const serialNumber = row.asset.serialNumber;
      const response = responses[row.key];
      if (!serialNumber || !response) {
        return inputs;
      }
      const trimmedRemarks = remarks[row.key]?.trim();
      const input: ResponseInput = { serialNumber, response };
      if (trimmedRemarks) {
        input.remarks = trimmedRemarks;
      }
      if (row.asset.model) {
        input.modelDescription = row.asset.model;
      }
      return [...inputs, input];
    }, []);
    const request: SubmitResponsesRequest = { campaignId, login, responses: responseInputs };
    if (responseInputs.some((input) => input.response === RETURN_PICKUP_RESPONSE)) {
      request.pickupAddress = pickupAddress;
    }
    await submit(request);
  }, [campaign, rows, responses, remarks, pickupAddress, login, submit]);

  const acknowledgeNoAssets = useCallback(async () => {
    const campaignId = campaign?.id;
    if (!campaignId) {
      return;
    }
    await submit({ campaignId, login, responses: [] });
  }, [campaign, login, submit]);

  const respondedCount = rows.filter((row) => Boolean(responses[row.key])).length;
  const isEveryRowAnswered = rows.length > 0 && respondedCount === rows.length;
  const isPickupAddressRequired = rows.some(
    (row) => responses[row.key] === RETURN_PICKUP_RESPONSE
  );

  return {
    rows,
    responses,
    remarks,
    pickupAddress,
    respondedCount,
    isEveryRowAnswered,
    isReadyToSave:
      isEveryRowAnswered && (!isPickupAddressRequired || isPickupAddressValid(pickupAddress)),
    isSubmitting: isPending,
    submitError: error,
    hasSubmitSucceeded,
    saveAttemptCount,
    onResponseChange,
    onRemarksChange,
    onPickupAddressChange,
    saveResponses,
    acknowledgeNoAssets,
  };
}
